import React, {
  useCallback, useRef, useEffect, useState,
} from 'react';
import * as Styles from './TablePlan.styles';
import withErrorBoundary from '@/utils/errorBoundary';
import useLocale from '@/hooks/useLocale';
import { GradientButton } from '@/pages/Studio/components';
import { IconAiPencil2 } from 'gatorcomponents';
import SnappyImageSvg from '../../media/snappy.svg';
import { useStudio } from '@/hooks/studio';

function useResize() {
  const [windowRect, setWindowRect] = useState({
    width: window.innerWidth,
  });

  useEffect(() => {
    const handleResize = () => {
      setWindowRect({
        width: window.innerWidth,
      });
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return windowRect;
}

const TablePlan = ({
  trialAvailable, planFree, planPro, planDiscountData, setDisplayModalFeaturePro, hasDiscount,
}) => {
  const { studioPlans: studioPlansLocale } = useLocale();
  const tablePlanLocale = studioPlansLocale.tablePlan;
  const windowRect = useResize();
  const { width } = windowRect;
  const mobile = width < 960;
  const { checkoutTrialWithCreditCard } = useStudio();
  const descTextRefs = useRef([]);
  const descHeaderPlan = useRef(null);
  const [lineCounts, setLineCounts] = useState([]);
  const [headerHeight, setHeaderHeight] = useState(0);

  useEffect(() => {
    if (descHeaderPlan.current) {
      setHeaderHeight(descHeaderPlan.current.offsetHeight);
    }
  }, []);

  useEffect(() => {
    const calculateLineCounts = () => {
      const lineCountsArray = tablePlanLocale.map((_, index) => {
        const element = descTextRefs.current[index];
        if (element) {
          const height = element.offsetHeight;
          return Math.ceil(height / 20);
        }
        return 0;
      });
      setLineCounts(lineCountsArray);
    };

    calculateLineCounts();

    const handleResize = () => {
      calculateLineCounts();

      if (descHeaderPlan.current) {
        setHeaderHeight(descHeaderPlan.current.offsetHeight);
      }
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [tablePlanLocale]);

  const removeCurrencyPrice = (price) => {
    if (!price) return '';

    return price.replace(planPro.currency, '');
  };

  const handlePurchasePlan = useCallback(() => {
    if (!trialAvailable) {
      checkoutTrialWithCreditCard({
        pid: planPro.planId, coupon: planDiscountData.name, couponAvailable: hasDiscount,
      });
      return;
    }

    setDisplayModalFeaturePro(true);
  }, [
    checkoutTrialWithCreditCard,
    setDisplayModalFeaturePro,
    trialAvailable,
    planPro.planId,
    hasDiscount,
    planDiscountData,
  ]);

  const mountTableItens = plan => (
    <Styles.TableInnerColumn plan={plan}>
      {tablePlanLocale.map((item, index) => (
        <Styles.TablePlanLine key={item.id}>
          <Styles.DescText
            lineCount={lineCounts[index] || 0}
            type="plan"
          >
            {mobile ? item[plan].mobile : item[plan].desktop}
          </Styles.DescText>
        </Styles.TablePlanLine>
      ))}
    </Styles.TableInnerColumn>
  );

  const HeaderDesc = () => (
    <Styles.HeaderPlan ref={descHeaderPlan} type="desc">
      <Styles.WrapperSnappyImage>
        <Styles.SnappyImage src={SnappyImageSvg} />
      </Styles.WrapperSnappyImage>
      <Styles.TitleHeaderDesc>
        {studioPlansLocale.headerDesc.title}
      </Styles.TitleHeaderDesc>
      <Styles.SubtitleHeaderDesc>
        {studioPlansLocale.headerDesc.subtitle}
      </Styles.SubtitleHeaderDesc>
      <Styles.WrapperPlanRecurrence>
        <Styles.TitlePlanRecurrence>
          {studioPlansLocale.headerDesc.planType}
        </Styles.TitlePlanRecurrence>
        <Styles.PlanRecurrence>
          {studioPlansLocale.headerDesc.planRecurrence}
        </Styles.PlanRecurrence>
      </Styles.WrapperPlanRecurrence>
    </Styles.HeaderPlan>
  );

  const HeaderFree = () => (
    <Styles.HeaderPlan
      type="plan"
      plan="free"
      style={{ height: !mobile ? `${headerHeight}px` : 'auto' }}
    >
      <Styles.PlanTitle>
        {planFree.name}
      </Styles.PlanTitle>
      <Styles.WrapperPlanPrice>
        <Styles.PlanCurrency>
          {planFree.currency}
        </Styles.PlanCurrency>
        <Styles.PlanPrice>
          {planFree.price}
        </Styles.PlanPrice>
      </Styles.WrapperPlanPrice>
      <Styles.PlanPriceDesc>
        {studioPlansLocale.planFree.priceDesc}
      </Styles.PlanPriceDesc>
    </Styles.HeaderPlan>
  );

  const HeaderPro = () => (
    <Styles.HeaderPlan
      type="plan"
      plan="pro"
      style={{ height: !mobile ? `${headerHeight}px` : 'auto' }}
    >
      <Styles.HeaderPlanPro>
        {trialAvailable ? studioPlansLocale.planPro.trial : studioPlansLocale.planPro.name}
        <IconAiPencil2 color="highPure" size="sm" />
      </Styles.HeaderPlanPro>
      <Styles.PlanTitle>
        {planPro.name}
      </Styles.PlanTitle>
      <Styles.WrapperPlanPrice>
        <Styles.PlanCurrency>
          {planPro.currency}
        </Styles.PlanCurrency>
        <Styles.PlanPrice>
          {removeCurrencyPrice(planPro.price)}
        </Styles.PlanPrice>
        <Styles.PlanPriceRecurrence>
          {studioPlansLocale.planPro.recurrence}
        </Styles.PlanPriceRecurrence>
      </Styles.WrapperPlanPrice>
      <Styles.WrapperPlanButton>
        <GradientButton
          testId="upgrade-to-pro-button"
          label={studioPlansLocale.planPro.buttonLabel}
          onClick={handlePurchasePlan}
          variant="primary"
          width="full"
        />
      </Styles.WrapperPlanButton>
    </Styles.HeaderPlan>
  );

  return (
    <Styles.WrapperTablePlan>
      <Styles.TablePlan>
        <Styles.TableColumn type="desc">
          <HeaderDesc />

          {!mobile && (
            <Styles.TableInnerColumn>
              {tablePlanLocale.map((item, index) => (
                <Styles.TablePlanLine type="desc" key={item.id}>
                  <Styles.DescText
                    ref={(el) => { descTextRefs.current[index] = el; }}
                  >
                    {item.desc}
                  </Styles.DescText>
                </Styles.TablePlanLine>
              ))}
            </Styles.TableInnerColumn>
          )}
        </Styles.TableColumn>

        <Styles.TableSpacing />

        <Styles.TableColumn type="plan" plan="free">
          <HeaderFree />
          {mountTableItens('free')}
        </Styles.TableColumn>

        <Styles.TableSpacing />

        <Styles.TableColumn type="plan" plan="pro">
          <HeaderPro />
          {mountTableItens('pro')}
        </Styles.TableColumn>
      </Styles.TablePlan>
    </Styles.WrapperTablePlan>
  );
};

export default withErrorBoundary(TablePlan, 'TablePlan');
